import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { get_reviews } from '../../store/reducers/homeReducer';

/**
 * Hook to enrich a flat array of products with rating_review and totalReview.
 * @param {Array} products - Array of product objects (must include _id)
 * @returns {Array} enrichedProducts - Array with same products plus rating_review and totalReview
 */
export function useEnrichProducts(products) {
  const dispatch = useDispatch();
  const [enriched, setEnriched] = useState([]);

  useEffect(() => {
    let canceled = false;
    async function fetchRatings() {
      if (!Array.isArray(products) || products.length === 0) {
        setEnriched([]);
        return;
      }
      const results = [];
      for (const product of products) {
        try {
          const res = await dispatch(
            get_reviews({ productId: product._id, pageNumber: 1 })
          ).unwrap();
          results.push({
            ...product,
            rating_review: res.rating_review || [],
            totalReview: res.totalReview || 0,
          });
        } catch {
          results.push({
            ...product,
            rating_review: [],
            totalReview: 0,
          });
        }
      }
      if (!canceled) setEnriched(results);
    }

    fetchRatings();
    return () => { canceled = true; };
  }, [products, dispatch]);

  return enriched;
}

/**
 * Hook to enrich a grouped array (array of product arrays) with rating data.
 * @param {Array<Array>} groupedProducts - Array of product arrays
 * @returns {Array<Array>} enrichedGroups - Nested arrays enriched
 */
export function useEnrichProductGroups(groupedProducts) {
  const dispatch = useDispatch();
  const [enrichedGroups, setEnrichedGroups] = useState([]);

  useEffect(() => {
    let canceled = false;
    async function fetchAll() {
      if (!Array.isArray(groupedProducts) || groupedProducts.length === 0) {
        setEnrichedGroups([]);
        return;
      }
      const allGroups = [];
      for (const group of groupedProducts) {
        const enrichedGroup = [];
        for (const product of group) {
          try {
            const res = await dispatch(
              get_reviews({ productId: product._id, pageNumber: 1 })
            ).unwrap();
            enrichedGroup.push({
              ...product,
              rating_review: res.rating_review || [],
              totalReview: res.totalReview || 0,
            });
          } catch {
            enrichedGroup.push({
              ...product,
              rating_review: [],
              totalReview: 0,
            });
          }
        }
        allGroups.push(enrichedGroup);
      }
      if (!canceled) setEnrichedGroups(allGroups);
    }

    fetchAll();
    return () => { canceled = true; };
  }, [groupedProducts, dispatch]);

  return enrichedGroups;
}
